import React, { useState, useEffect } from 'react';
import { Search, X, Filter, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import { cn } from '@/lib/utils';

interface MenuSearchProps {
  onSearch: (searchTerm: string, filters: SearchFilters) => void;
  sections: Array<{ id: string; name: string }>;
  totalItems: number;
  filteredItems: number;
  className?: string;
}

export interface SearchFilters {
  sections: string[];
  dietary: string[];
  itemType: 'all' | 'food' | 'beverage';
  priceRange: { min?: number; max?: number };
}

const DIETARY_OPTIONS = [
  { value: 'vegetarian', label: 'Vegetarian' },
  { value: 'vegan', label: 'Vegan' },
  { value: 'gluten_free', label: 'Gluten Free' },
];

export const MenuSearch: React.FC<MenuSearchProps> = ({
  onSearch,
  sections,
  totalItems,
  filteredItems,
  className
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<SearchFilters>({
    sections: [],
    dietary: [],
    itemType: 'all',
    priceRange: {}
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      onSearch(searchTerm, filters);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, filters, onSearch]);

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilters({
      sections: [],
      dietary: [],
      itemType: 'all',
      priceRange: {}
    });
  };

  const toggleSection = (sectionId: string) => {
    setFilters(prev => ({
      ...prev,
      sections: prev.sections.includes(sectionId)
        ? prev.sections.filter(id => id !== sectionId)
        : [...prev.sections, sectionId]
    }));
  };

  const toggleDietary = (dietary: string) => {
    setFilters(prev => ({
      ...prev,
      dietary: prev.dietary.includes(dietary)
        ? prev.dietary.filter(d => d !== dietary)
        : [...prev.dietary, dietary]
    }));
  };

  const activeFilterCount = 
    filters.sections.length + 
    filters.dietary.length + 
    (filters.itemType !== 'all' ? 1 : 0) +
    (filters.priceRange.min || filters.priceRange.max ? 1 : 0);

  return (
    <div className={cn("bg-white rounded-xl border border-gray-200 shadow-sm p-4", className)}>
      <div className="flex items-center gap-4">
        {/* Search Input */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search menu items by name, description, or price..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-gray-50 border border-gray-200 rounded-lg pl-10 pr-10 py-2 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913]/20 transition-all"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Filter Dropdown */}
        <DropdownMenu open={isFilterOpen} onOpenChange={setIsFilterOpen}>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-100 transition-all">
              <Filter className="w-4 h-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-[#FDB913]/20 text-[#FDB913] rounded-full font-medium">
                  {activeFilterCount}
                </span>
              )}
              <ChevronDown className="w-4 h-4 ml-1" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            {/* Item Type Filter */}
            <DropdownMenuLabel>Item Type</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => setFilters(prev => ({ ...prev, itemType: 'all' }))}>
              <span className={cn("ml-2", filters.itemType === 'all' && "font-semibold")}>
                All Items
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setFilters(prev => ({ ...prev, itemType: 'food' }))}>
              <span className={cn("ml-2", filters.itemType === 'food' && "font-semibold")}>
                Food Only
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setFilters(prev => ({ ...prev, itemType: 'beverage' }))}>
              <span className={cn("ml-2", filters.itemType === 'beverage' && "font-semibold")}>
                Beverages Only
              </span>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {/* Section Filter */}
            <DropdownMenuLabel>Sections</DropdownMenuLabel>
            {sections.map(section => (
              <DropdownMenuCheckboxItem
                key={section.id}
                checked={filters.sections.includes(section.id)}
                onCheckedChange={() => toggleSection(section.id)}
              >
                {section.name}
              </DropdownMenuCheckboxItem>
            ))}

            <DropdownMenuSeparator />

            {/* Dietary Filter */}
            <DropdownMenuLabel>Dietary Restrictions</DropdownMenuLabel>
            {DIETARY_OPTIONS.map(option => (
              <DropdownMenuCheckboxItem
                key={option.value}
                checked={filters.dietary.includes(option.value)}
                onCheckedChange={() => toggleDietary(option.value)}
              >
                {option.label}
              </DropdownMenuCheckboxItem>
            ))}

            {activeFilterCount > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleClearSearch}>
                  <X className="w-4 h-4 mr-2" />
                  Clear All Filters
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Results Count */}
        <div className="text-sm text-gray-500">
          {filteredItems !== totalItems ? (
            <span>
              Showing <span className="font-semibold text-gray-900">{filteredItems}</span> of{' '}
              <span className="font-semibold text-gray-900">{totalItems}</span> items
            </span>
          ) : (
            <span>
              <span className="font-semibold text-gray-900">{totalItems}</span> items
            </span>
          )}
        </div>
      </div>

      {/* Active Filters */}
      {(searchTerm || activeFilterCount > 0) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {searchTerm && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#FDB913]/10 text-[#b8860b] rounded-full text-sm font-medium">
              Search: "{searchTerm}"
              <button onClick={() => setSearchTerm('')} className="hover:text-[#FDB913]">
                <X className="w-3 h-3 ml-1" />
              </button>
            </span>
          )}
          {filters.itemType !== 'all' && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#FDB913]/10 text-[#b8860b] rounded-full text-sm font-medium">
              Type: {filters.itemType}
              <button onClick={() => setFilters(prev => ({ ...prev, itemType: 'all' }))} className="hover:text-[#FDB913]">
                <X className="w-3 h-3 ml-1" />
              </button>
            </span>
          )}
          {filters.sections.map(sectionId => {
            const section = sections.find(s => s.id === sectionId);
            return section ? (
              <span key={sectionId} className="inline-flex items-center gap-1 px-2 py-1 bg-[#FDB913]/10 text-[#b8860b] rounded-full text-sm font-medium">
                Section: {section.name}
                <button onClick={() => toggleSection(sectionId)} className="hover:text-[#FDB913]">
                  <X className="w-3 h-3 ml-1" />
                </button>
              </span>
            ) : null;
          })}
          {filters.dietary.map(dietary => (
            <span key={dietary} className="inline-flex items-center gap-1 px-2 py-1 bg-[#FDB913]/10 text-[#b8860b] rounded-full text-sm font-medium">
              {DIETARY_OPTIONS.find(d => d.value === dietary)?.label}
              <button onClick={() => toggleDietary(dietary)} className="hover:text-[#FDB913]">
                <X className="w-3 h-3 ml-1" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};